import { LitElement, html, nothing } from "lit";
import { customElement, property, state, query } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import "@phosphor-icons/webcomponents/PhSmiley";
import "@phosphor-icons/webcomponents/PhPaperPlaneTilt";
import "@phosphor-icons/webcomponents/PhPlus";
import "@phosphor-icons/webcomponents/PhKeyboard";
import "@phosphor-icons/webcomponents/PhImages";
import "@phosphor-icons/webcomponents/PhCamera";
import "@phosphor-icons/webcomponents/PhFile";
import "@phosphor-icons/webcomponents/PhX";
import "@phosphor-icons/webcomponents/PhPlay";
import "@phosphor-icons/webcomponents/PhPause";
import { styles } from "./chat-message-input.css";
import { uploadFile, FileUploadError, formatUploadError } from "ui-common/services/file-upload";
import { formatFileSize, CHAT_ATTACHMENT_MIME_TYPES } from "helpers/fileValidation";

const IMAGE_VIDEO_MIME_TYPES = CHAT_ATTACHMENT_MIME_TYPES.filter(
  (m) => m.startsWith("image/") || m.startsWith("video/"),
).join(",");

const DOCUMENT_MIME_TYPES = CHAT_ATTACHMENT_MIME_TYPES.filter(
  (m) => !m.startsWith("image/") && !m.startsWith("video/"),
).join(",");

interface PendingAttachment {
  tempId: string;
  file: File;
  progress: number;
  fileId?: string;
  error?: string;
  // Local object URL for image/video pre-send preview. Documents have none.
  previewUrl?: string;
}

export interface SendMessageAttachment {
  fileId: string;
  originalName: string;
  sizeBytes: number;
}

/**
 * Chat message input with pill-style textarea, emoji, send, and optional file attachments.
 *
 * When `allow-attachments` is set, a "+" button on the left opens a popup with
 * source-specific options (Photos & Videos, Camera, Document) instead of a single
 * file picker. Selected files upload immediately and appear as removable chips
 * above the textarea.
 *
 * @fires send-message - { message, notifyByEmail, attachments }
 * @fires emoji-toggle
 */
@customElement("chat-message-input")
export class ChatMessageInput extends LitElement {
  static override styles = styles;

  @query("textarea") private textareaElement!: HTMLTextAreaElement;
  @query('input[data-source="photos"]') private photosInput?: HTMLInputElement;
  @query('input[data-source="camera"]') private cameraInput?: HTMLInputElement;
  @query('input[data-source="document"]') private documentInput?: HTMLInputElement;
  @query("video.media-preview-media") private previewVideo?: HTMLVideoElement;
  @query(".media-preview-overlay") private previewOverlayEl?: HTMLElement;

  @property({ type: String }) placeholder = "Send a message...";
  @property({ type: String, attribute: "avatar-url" }) avatarUrl?: string;
  @property({ type: Boolean }) disabled = false;
  @property({ type: Boolean }) sending = false;
  @property({ type: Boolean, attribute: "show-notify-toggle" }) showNotifyToggle = false;
  @property({ type: String, attribute: "notify-label" }) notifyLabel = "Notify fans via email";
  @property({ type: Boolean, attribute: "allow-attachments" }) allowAttachments = false;
  @property({ type: String, attribute: "photos-label" }) photosLabel = "Photos & Videos";
  @property({ type: String, attribute: "camera-label" }) cameraLabel = "Camera";
  @property({ type: String, attribute: "document-label" }) documentLabel = "Document";

  @state() private value = "";
  @state() private notifyByEmail = false;
  @state() private isSingleLine = true;
  @state() private pendingAttachments: PendingAttachment[] = [];
  @state() private optionsOpen = false;
  // tempId of the image/video currently shown in the full pre-send preview
  // overlay (undefined = no preview). Only one media is previewed at a time.
  @state() private previewTempId?: string;
  @state() private videoPlaying = false;
  // The Camera option relies on `<input capture>`, which only opens the native
  // camera on touch devices. On desktop it silently falls back to the file
  // picker — identical to Photos & Videos — so we hide it there.
  @state() private isTouchDevice = false;

  private static readonly MAX_LENGTH = 1000;
  private static readonly COUNTER_THRESHOLD = 50;

  private touchMediaQuery?: MediaQueryList;

  private outsideClickHandler = (e: MouseEvent): void => {
    if (!this.optionsOpen) return;
    const path = e.composedPath();
    if (path.includes(this)) return;
    this.optionsOpen = false;
  };

  private touchChangeHandler = (e: MediaQueryListEvent): void => {
    this.isTouchDevice = e.matches;
  };

  override connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener("click", this.outsideClickHandler, true);
    this.touchMediaQuery = window.matchMedia("(pointer: coarse)");
    this.isTouchDevice = this.touchMediaQuery.matches;
    this.touchMediaQuery.addEventListener("change", this.touchChangeHandler);
  }

  override disconnectedCallback(): void {
    document.removeEventListener("click", this.outsideClickHandler, true);
    this.touchMediaQuery?.removeEventListener("change", this.touchChangeHandler);
    this.revokeAllPreviews();
    super.disconnectedCallback();
  }

  protected override updated(): void {
    // The preview is a Popover (top layer) so it escapes ancestor transforms and
    // covers the full viewport. Show it once it is rendered for the active media.
    const overlay = this.previewOverlayEl;
    if (this.previewAttachment && overlay && !overlay.matches(":popover-open")) {
      overlay.showPopover();
    }
  }

  private revokeAllPreviews(): void {
    for (const a of this.pendingAttachments) {
      if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
    }
  }

  private isPreviewable(file: File): boolean {
    return file.type.startsWith("image/") || file.type.startsWith("video/");
  }

  private get charCount(): number {
    return this.value.length;
  }

  private get isOverLimit(): boolean {
    return this.charCount > ChatMessageInput.MAX_LENGTH;
  }

  private get showCharCounter(): boolean {
    return this.charCount > ChatMessageInput.MAX_LENGTH - ChatMessageInput.COUNTER_THRESHOLD;
  }

  private get readyAttachments(): PendingAttachment[] {
    return this.pendingAttachments.filter((a) => a.fileId && !a.error);
  }

  private get uploadsInFlight(): boolean {
    return this.pendingAttachments.some((a) => !a.fileId && !a.error);
  }

  private get canSend(): boolean {
    const hasText = this.value.trim().length > 0;
    const hasReadyAttachment = this.readyAttachments.length > 0;
    return (
      (hasText || hasReadyAttachment) && !this.uploadsInFlight && !this.isOverLimit && !this.disabled && !this.sending
    );
  }

  private handleInput(e: Event): void {
    const textarea = e.target as HTMLTextAreaElement;
    this.value = textarea.value;
    this.autoResize();
  }

  private handleKeyDown(e: KeyboardEvent): void {
    if (e.key !== "Enter") return;
    if (e.isComposing) return;

    const isTouchPrimary = window.matchMedia("(pointer: coarse)").matches;

    if (isTouchPrimary) {
      if (e.shiftKey) {
        e.preventDefault();
        this.send();
      }
      return;
    }

    if (!e.shiftKey) {
      e.preventDefault();
      this.send();
    }
  }

  private autoResize(): void {
    const textarea = this.textareaElement;
    if (!textarea) return;
    textarea.style.height = "auto";
    const scrollHeight = textarea.scrollHeight;
    this.isSingleLine = scrollHeight <= 28;
    textarea.style.height = `${scrollHeight}px`;
  }

  private send(): void {
    if (!this.canSend) return;

    const message = this.value.trim();
    const notifyByEmail = this.notifyByEmail;
    const attachments: SendMessageAttachment[] = this.readyAttachments.map((a) => ({
      fileId: a.fileId!,
      originalName: a.file.name,
      sizeBytes: a.file.size,
    }));

    const textarea = this.textareaElement;
    if (textarea) {
      textarea.value = "";
    }

    this.dispatchEvent(
      new CustomEvent("send-message", {
        detail: { message, notifyByEmail, attachments },
        bubbles: true,
        composed: true,
      }),
    );

    this.revokeAllPreviews();
    this.value = "";
    this.isSingleLine = true;
    this.notifyByEmail = false;
    this.pendingAttachments = [];
    this.optionsOpen = false;
    this.previewTempId = undefined;
    this.videoPlaying = false;

    this.updateComplete.then(() => {
      if (this.textareaElement) {
        this.textareaElement.style.height = "auto";
        this.textareaElement.focus();
      }
    });
  }

  private handleEmojiClick(): void {
    this.dispatchEvent(
      new CustomEvent("emoji-toggle", {
        bubbles: true,
        composed: true,
      }),
    );
  }

  private handleAttachClick(): void {
    this.optionsOpen = !this.optionsOpen;
    // Closing via the keyboard icon should bring focus back to the textarea so
    // the soft keyboard reappears on touch devices.
    if (!this.optionsOpen) {
      this.updateComplete.then(() => this.focus());
    }
  }

  private handleOptionClick(source: "photos" | "camera" | "document"): void {
    this.optionsOpen = false;
    const input = source === "photos" ? this.photosInput : source === "camera" ? this.cameraInput : this.documentInput;
    input?.click();
  }

  private async handleFileSelect(e: Event): Promise<void> {
    const input = e.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = "";
    for (const file of files) {
      void this.uploadAttachment(file);
    }
  }

  private async uploadAttachment(file: File): Promise<void> {
    const tempId = `att-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const previewUrl = this.isPreviewable(file) ? URL.createObjectURL(file) : undefined;
    this.pendingAttachments = [...this.pendingAttachments, { tempId, file, progress: 0, previewUrl }];

    // Image/video selections open the full pre-send preview immediately while
    // the upload continues in the background. Documents keep the inline chip.
    if (previewUrl) {
      this.optionsOpen = false;
      this.previewTempId = tempId;
      this.videoPlaying = false;
    }

    try {
      const result = await uploadFile(file, "chat_attachment", {
        onProgress: (progress) => {
          this.pendingAttachments = this.pendingAttachments.map((a) => (a.tempId === tempId ? { ...a, progress } : a));
        },
      });
      this.pendingAttachments = this.pendingAttachments.map((a) =>
        a.tempId === tempId ? { ...a, fileId: result.fileId, progress: 100 } : a,
      );
    } catch (err) {
      const message = err instanceof FileUploadError ? formatUploadError(err) : "Upload failed";
      this.pendingAttachments = this.pendingAttachments.map((a) =>
        a.tempId === tempId ? { ...a, error: message } : a,
      );
    }
  }

  private removeAttachment(tempId: string): void {
    const att = this.pendingAttachments.find((a) => a.tempId === tempId);
    if (att?.previewUrl) URL.revokeObjectURL(att.previewUrl);
    this.pendingAttachments = this.pendingAttachments.filter((a) => a.tempId !== tempId);
    if (this.previewTempId === tempId) {
      this.previewTempId = undefined;
      this.videoPlaying = false;
    }
  }

  private toggleVideo(): void {
    const video = this.previewVideo;
    if (!video) return;
    if (video.paused) {
      void video.play();
    } else {
      video.pause();
    }
  }

  public override focus(): void {
    this.textareaElement?.focus();
  }

  public clear(): void {
    this.revokeAllPreviews();
    this.value = "";
    this.isSingleLine = true;
    this.pendingAttachments = [];
    this.optionsOpen = false;
    this.previewTempId = undefined;
    this.videoPlaying = false;
    if (this.textareaElement) {
      this.textareaElement.style.height = "auto";
    }
  }

  public insertText(text: string): void {
    this.value += text;
    this.updateComplete.then(() => {
      this.autoResize();
      this.focus();
    });
  }

  private handleNotifyToggle(e: Event): void {
    this.notifyByEmail = (e.target as HTMLInputElement).checked;
  }

  private renderAttachmentChips() {
    // Media (image/video) is shown in the full preview overlay, not as a chip.
    const chipAttachments = this.pendingAttachments.filter((a) => !a.previewUrl);
    if (chipAttachments.length === 0) return nothing;
    return html`
      <ul class="attachment-chips" aria-live="polite">
        ${chipAttachments.map(
          (a) => html`
            <li
              class=${classMap({
                chip: true,
                uploading: !a.fileId && !a.error,
                error: Boolean(a.error),
              })}
            >
              <span class="chip-name" title=${a.file.name}>${a.file.name}</span>
              <span class="chip-meta">
                ${a.error ? a.error : a.fileId ? formatFileSize(a.file.size) : `${Math.round(a.progress)}%`}
              </span>
              <button
                class="chip-remove"
                type="button"
                @click=${() => this.removeAttachment(a.tempId)}
                aria-label="Remove attachment"
              >
                <ph-x size="14" weight="bold"></ph-x>
              </button>
            </li>
          `,
        )}
      </ul>
    `;
  }

  private renderAttachmentOptions() {
    if (!this.allowAttachments || !this.optionsOpen) return nothing;
    return html`
      <div class="attachment-options" role="menu">
        <button
          class="attachment-option"
          type="button"
          role="menuitem"
          @click=${() => this.handleOptionClick("photos")}
        >
          <span class="attachment-option-circle">
            <ph-images size="32" weight="fill"></ph-images>
          </span>
          <span class="attachment-option-label">${this.photosLabel}</span>
        </button>
        ${this.isTouchDevice
          ? html`
              <button
                class="attachment-option"
                type="button"
                role="menuitem"
                @click=${() => this.handleOptionClick("camera")}
              >
                <span class="attachment-option-circle">
                  <ph-camera size="32" weight="fill"></ph-camera>
                </span>
                <span class="attachment-option-label">${this.cameraLabel}</span>
              </button>
            `
          : nothing}
        <button
          class="attachment-option"
          type="button"
          role="menuitem"
          @click=${() => this.handleOptionClick("document")}
        >
          <span class="attachment-option-circle">
            <ph-file size="32" weight="fill"></ph-file>
          </span>
          <span class="attachment-option-label">${this.documentLabel}</span>
        </button>
      </div>
    `;
  }

  private get previewAttachment(): PendingAttachment | undefined {
    if (!this.previewTempId) return undefined;
    return this.pendingAttachments.find((a) => a.tempId === this.previewTempId && a.previewUrl);
  }

  private renderPreviewOverlay(att: PendingAttachment): unknown {
    const isVideo = att.file.type.startsWith("video/");
    const uploading = !att.fileId && !att.error;
    return html`
      <div class="media-preview-overlay" popover="manual">
        <div class="media-preview-header">
          <button
            class="media-preview-close"
            type="button"
            @click=${() => this.removeAttachment(att.tempId)}
            aria-label="Close preview"
          >
            <ph-x size="20" weight="bold"></ph-x>
          </button>
          <span class="media-preview-title" title=${att.file.name}>${att.file.name}</span>
          ${isVideo
            ? html`<button
                class="media-preview-toggle"
                type="button"
                @click=${this.toggleVideo}
                aria-label=${this.videoPlaying ? "Pause video" : "Play video"}
              >
                ${this.videoPlaying
                  ? html`<ph-pause size="22" weight="fill"></ph-pause>`
                  : html`<ph-play size="22" weight="fill"></ph-play>`}
              </button>`
            : html`<span class="media-preview-header-spacer"></span>`}
        </div>

        <div class="media-preview-body">
          ${isVideo
            ? html`
                <video
                  class="media-preview-media"
                  src=${att.previewUrl ?? ""}
                  playsinline
                  @click=${this.toggleVideo}
                  @play=${() => (this.videoPlaying = true)}
                  @pause=${() => (this.videoPlaying = false)}
                ></video>
                ${this.videoPlaying
                  ? nothing
                  : html`<button
                      class="media-preview-play-overlay"
                      type="button"
                      @click=${this.toggleVideo}
                      aria-label="Play video"
                    >
                      <ph-play size="40" weight="fill"></ph-play>
                    </button>`}
              `
            : html`<img class="media-preview-media" src=${att.previewUrl ?? ""} alt=${att.file.name} />`}
        </div>

        <div class="media-preview-input">
          ${att.error
            ? html`<span class="media-preview-error">${att.error}</span>`
            : uploading
              ? html`<span class="media-preview-progress">${Math.round(att.progress)}%</span>`
              : nothing}
          <div class=${classMap({ container: true, "single-line": this.isSingleLine })}>
            <div class="pill">
              <textarea
                class="message-input"
                .value=${this.value}
                placeholder=${this.placeholder}
                ?disabled=${this.disabled || this.sending}
                @input=${this.handleInput}
                @keydown=${this.handleKeyDown}
                rows="1"
              ></textarea>
            </div>
            <button
              class="send-button"
              type="button"
              ?disabled=${!this.canSend}
              @click=${this.send}
              aria-label="Send message"
            >
              <ph-paper-plane-tilt size="18" weight="fill"></ph-paper-plane-tilt>
            </button>
          </div>
        </div>
      </div>
    `;
  }

  override render() {
    const preview = this.previewAttachment;
    if (preview) {
      return this.renderPreviewOverlay(preview);
    }
    return html`
      <div class="input-wrapper">
        ${this.renderAttachmentChips()}
        <div
          class=${classMap({
            container: true,
            "single-line": this.isSingleLine,
          })}
        >
          ${this.allowAttachments
            ? html`
                <button
                  class="attach-button"
                  type="button"
                  ?disabled=${this.disabled || this.sending}
                  @click=${this.handleAttachClick}
                  aria-label=${this.optionsOpen ? "Show keyboard" : "Add attachment"}
                  aria-expanded=${this.optionsOpen}
                >
                  ${this.optionsOpen
                    ? html`<ph-keyboard size="22" weight="bold"></ph-keyboard>`
                    : html`<ph-plus size="20" weight="bold"></ph-plus>`}
                </button>
                <input
                  type="file"
                  hidden
                  data-source="photos"
                  accept=${IMAGE_VIDEO_MIME_TYPES}
                  @change=${this.handleFileSelect}
                />
                <input
                  type="file"
                  hidden
                  data-source="camera"
                  accept="image/*,video/*"
                  capture="environment"
                  @change=${this.handleFileSelect}
                />
                <input
                  type="file"
                  hidden
                  multiple
                  data-source="document"
                  accept=${DOCUMENT_MIME_TYPES}
                  @change=${this.handleFileSelect}
                />
              `
            : nothing}

          <div class="pill">
            <textarea
              class="message-input"
              .value=${this.value}
              placeholder=${this.placeholder}
              ?disabled=${this.disabled || this.sending}
              @input=${this.handleInput}
              @keydown=${this.handleKeyDown}
              rows="1"
            ></textarea>
            <button
              class="emoji-button"
              type="button"
              ?disabled=${this.disabled || this.sending}
              @click=${this.handleEmojiClick}
              aria-label="Open emoji picker"
            >
              <ph-smiley size="20" weight="bold"></ph-smiley>
            </button>
          </div>

          <button
            class="send-button"
            type="button"
            ?disabled=${!this.canSend}
            @click=${this.send}
            aria-label="Send message"
          >
            <ph-paper-plane-tilt size="18" weight="fill"></ph-paper-plane-tilt>
          </button>
        </div>
        ${this.renderAttachmentOptions()}
        ${this.showCharCounter
          ? html`<span class=${classMap({ "char-counter": true, "over-limit": this.isOverLimit })}
              >${this.charCount}/${ChatMessageInput.MAX_LENGTH}</span
            >`
          : nothing}
        ${this.showNotifyToggle
          ? html`
              <label class="notify-toggle">
                <input type="checkbox" .checked=${this.notifyByEmail} @change=${this.handleNotifyToggle} />
                <span class="notify-label">${this.notifyLabel}</span>
              </label>
            `
          : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "chat-message-input": ChatMessageInput;
  }
}
